import React, { useEffect, useRef, useState } from "react";
import { Store, ImagePlus, Trash2, Download, Save } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ASSET = (process.env.REACT_APP_BACKEND_URL || "");
const FIELDS = [
  ["business_name", "Nome attività"], ["sector", "Settore"], ["city", "Città"],
  ["address", "Indirizzo"], ["phone", "Telefono"], ["public_email", "Email pubblica"],
  ["website", "Sito web"], ["social", "Profilo social"],
];

export const MyBusiness = () => {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoInput = useRef(null);
  const photoInput = useRef(null);

  const load = () => api.get("/me/business").then(({ data }) => setP(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(FIELDS.map(([k]) => [k, p[k] || ""]));
      const { data } = await api.put("/me/business", body);
      setP(data); toast.success("Dati salvati.");
    } catch { toast.error("Impossibile salvare."); }
    finally { setSaving(false); }
  };
  const upload = async (file, kind) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    try { await api.post("/me/business/asset", fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); toast.success("Caricato."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Caricamento non riuscito."); }
  };
  const del = async (id) => { try { await api.delete(`/me/business/asset/${id}`); await load(); } catch { /* ignore */ } };
  const dl = (a) => { const link = document.createElement("a"); link.href = ASSET + a.url; link.download = a.filename || "file"; link.target = "_blank"; link.click(); };

  if (!p) return null;
  return (
    <div data-testid="my-business" className="mt-12">
      <h2 className="text-xl font-bold font-heading text-brand-navy flex items-center gap-2"><Store className="h-5 w-5 text-brand-electric" /> La mia attività</h2>
      <p className="text-sm text-brand-navy/50 mt-1">Salva i tuoi dati e materiali una volta sola: li ritroverai già pronti nei prossimi progetti.</p>
      <div className="mt-5 rounded-2xl border border-brand-navy/10 bg-white p-6">
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, label]) => (
            <div key={k}><Label>{label}</Label>
              <Input data-testid={`biz-${k}`} value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} className="mt-1" /></div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="biz-save" onClick={save} disabled={saving} className="rounded-full bg-brand-electric text-white gap-1.5"><Save className="h-4 w-4" /> Salva dati</Button>
        </div>

        <div className="mt-6 border-t border-brand-navy/10 pt-5">
          <div className="flex flex-wrap gap-3 mb-3">
            <input ref={logoInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0], "logo")} />
            <input ref={photoInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0], "photo")} />
            <Button data-testid="biz-add-logo" onClick={() => logoInput.current?.click()} variant="outline" className="rounded-full gap-1.5"><ImagePlus className="h-4 w-4" /> Aggiungi logo</Button>
            <Button data-testid="biz-add-photo" onClick={() => photoInput.current?.click()} variant="outline" className="rounded-full gap-1.5"><ImagePlus className="h-4 w-4" /> Aggiungi immagine</Button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {p.assets?.map((a) => (
              <div key={a.id} data-testid={`biz-asset-${a.id}`} className="rounded-xl border border-brand-navy/10 bg-brand-light p-2">
                <div className="aspect-square flex items-center justify-center"><img src={ASSET + a.url} alt={a.kind} className="w-full h-full object-contain" /></div>
                <p className="text-[10px] text-brand-navy/50 mt-1 truncate">{a.kind} · {a.filename}</p>
                <div className="flex gap-1 mt-1">
                  <button data-testid={`biz-dl-${a.id}`} onClick={() => dl(a)} className="flex-1 text-[10px] text-brand-navy/70 border border-brand-navy/15 rounded-full py-1 flex items-center justify-center gap-1"><Download className="h-3 w-3" /> Scarica</button>
                  <button data-testid={`biz-del-${a.id}`} onClick={() => del(a.id)} className="flex-1 text-[10px] text-red-500 border border-red-200 rounded-full py-1 flex items-center justify-center gap-1"><Trash2 className="h-3 w-3" /> Elimina</button>
                </div>
              </div>
            ))}
            {!p.assets?.length && <p className="col-span-full text-sm text-brand-navy/40 py-4">Nessun materiale salvato.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyBusiness;
